import CitySkylinePanel from "@/components/CitySkylinePanel";

/**
 * Compact "nearest Texas metro" banner for the Plans results page -- residential/city photo
 * pairing, same visual language as the Home hero (Hero Background Design.md) but scaled down
 * to a contained, rounded-card banner rather than a second full-bleed hero, to fit the Plans
 * page's document-first component language (Design Specification.md Section 3.5).
 *
 * This is a named, scoped extension of the Home-hero photography exception to one additional
 * surface -- see index.css's `.city-match-*` block header comment. Flagged to Martha-BRM for
 * formal ratification (same basis as the already-ratified Home hero: real, specifically
 * licensed photos, Owner-directed feature, not a general stock-photography policy change).
 *
 * `cityKey` is the resolved `city_key` from GET /api/nearest-city (see Plans.jsx), or null.
 * This component makes no fallback decision of its own for a null key -- Plans.jsx already
 * decided that (no ZIP, malformed ZIP, or a ZIP outside the 55-mile radius of all 6 metros
 * all resolve to null before reaching here) -- it renders a neutral, photo-free brand-gradient
 * panel instead of guessing a city, per this project's standing "never present an estimate as
 * a fact" rule. A truthy but unrecognized key (a real metro the org hasn't sourced/graded a
 * photo for yet -- see frontend/src/lib/citySkylines.js) is handled by CitySkylinePanel itself,
 * which already falls back to the real, licensed Houston photo rather than a fabricated image
 * of a city that was never actually photographed.
 *
 * Residential panel is always the same real, licensed photo regardless of city (product
 * decision, see project memory 2026-09-24) -- only the right-hand city slot ever changes.
 * Decorative only: aria-hidden/presentation on the wrapper, alt="" on every image, matching
 * the Home hero's accessibility treatment exactly.
 */
export default function CityMatchBanner({ cityKey }) {
  return (
    <div className="city-match-banner" aria-hidden="true" role="presentation" data-testid="city-match-banner">
      <picture>
        <source srcSet="/hero/residential-panel.webp" type="image/webp" />
        <img
          src="/hero/residential-panel.jpg"
          alt=""
          width={1400}
          height={833}
          className="city-match-residential"
          loading="eager"
          data-testid="city-match-residential"
        />
      </picture>
      {cityKey ? (
        <CitySkylinePanel
          key={cityKey}
          city={cityKey}
          className="city-match-city"
          testId="city-match-city"
          eager
        />
      ) : (
        <div className="city-match-city-neutral" data-testid="city-match-city-neutral" />
      )}
      <div className="city-match-scrim" />
      <div className="city-match-accent" />
    </div>
  );
}
